import React, { FC, ReactElement } from 'react';
import { Theme } from '@mui/material/styles';
import { Link as RouterLink } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
// Components
import Typography, { TypographyProps } from '@mui/material/Typography';
// Resources
import { clsx } from 'clsx';
import { useBreakpoints } from '../customHooks/useBreakpoints';
import { isWebView } from '../utils/webview';
import colors from '../resources/style/colors';

export interface TextComponentProps extends TypographyProps {
    text: string;
    secondText?: string;
    pathSecondText?: string;
    secondTextColor?: 'blue' | 'lightblue' | 'grey' | 'red' | 'white' | 'black' | 'orange' | 'lightgrey' | 'default';
    onPathClick?: () => any;
    customColor?:
        | 'blue'
        | 'lightblue'
        | 'grey'
        | 'red'
        | 'white'
        | 'black'
        | 'orange'
        | 'lightgrey'
        | 'bothWhite'
        | 'default';
    font?: 'madras' | 'madrasRegular' | 'roboto';
    decoration?: 'lineThrough' | 'italic';
    component?: React.ElementType<any>;
    children?: React.ReactNode;
    containerClassName?: string;
}

const TextComponent: FC<TextComponentProps> = ({
    text,
    secondText,
    pathSecondText = null,
    secondTextColor = 'lightblue',
    onPathClick,
    className,
    customColor,
    font,
    decoration,
    children,
    containerClassName,
    ...rest
}): ReactElement => {
    const classes = useStyles();
    const { isMobileSize } = useBreakpoints();
    const isWebview = isWebView();

    const mergeStyle =
        rest.style?.fontSize && isMobileSize
            ? {
                  ...rest.style,
                  fontSize:
                      (typeof rest.style.fontSize === 'string'
                          ? (rest.style.fontSize.replace(/\D*/, '') as unknown as number)
                          : (rest.style.fontSize as number)) * 1, // 0.85,
              }
            : { ...rest.style };

    const mergeClassName = clsx(
        className,
        classes[customColor],
        classes[font],
        classes[decoration],
        rest.onClick ? classes.onClick : {},
    );

    if (rest.dangerouslySetInnerHTML) {
        return (
            <Typography
                color="primary"
                {...rest}
                style={mergeStyle}
                className={mergeClassName}
                dangerouslySetInnerHTML={rest.dangerouslySetInnerHTML}
            />
        );
    }

    const secondTextFormatted = text?.endsWith(' ') || text?.startsWith(' ') ? secondText : ` ${secondText}`;

    return (
        <div className={clsx(classes.flexDisplay, isWebview && classes.noSelection, containerClassName)}>
            <Typography color="primary" {...rest} style={mergeStyle} className={mergeClassName}>
                {text}
                {secondText && pathSecondText && (
                    <RouterLink to={pathSecondText} className={classes.noDecoration} onClick={onPathClick}>
                        <span className={classes.lightblue}>{secondTextFormatted}</span>
                    </RouterLink>
                )}
                {secondText && !pathSecondText && (
                    <span
                        onClick={onPathClick}
                        className={clsx(classes[secondTextColor], onPathClick && classes.onClick)}
                    >
                        {secondTextFormatted}
                    </span>
                )}
                {children}
            </Typography>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        flexDisplay: {
            display: 'flex',
        },
        noSelection: {
            userSelect: 'none',
        },
        blue: {
            color: theme.palette.secondary.main,
        },
        lightblue: {
            color: theme.palette.colors.text.accent,
        },
        grey: {
            color: theme.palette.colors.text.label,
        },
        lightgrey: {
            color: theme.palette.colors.icon.lightGrey,
        },
        red: {
            color: theme.palette.colors.text.error,
        },
        white: {
            color: theme.palette.secondary.contrastText,
        },
        bothWhite: {
            color: colors.darkWhite,
        },
        black: {
            color: theme.palette.primary.main,
        },
        orange: {
            color: theme.palette.colors.text.warning,
        },
        default: {},
        madras: {
            fontFamily: 'Madras',
        },
        madrasRegular: {
            fontFamily: 'Madras-Regular',
        },
        roboto: {
            fontFamily: 'Roboto',
        },
        noDecoration: {
            textDecoration: 'none',
        },
        lineThrough: {
            textDecoration: 'line-through',
        },
        italic: {
            fontStyle: 'italic',
        },
        onClick: {
            '&:hover': {
                cursor: 'pointer',
            },
        },
    }),
);

interface TextType {
    Body1: (props: TextComponentProps) => JSX.Element;
    Body2: (props: TextComponentProps) => JSX.Element;
    Subtitle1: (props: TextComponentProps) => JSX.Element;
    Subtitle2: (props: TextComponentProps) => JSX.Element;
    H1: (props: TextComponentProps) => JSX.Element;
    H2: (props: TextComponentProps) => JSX.Element;
    H3: (props: TextComponentProps) => JSX.Element;
    H4: (props: TextComponentProps) => JSX.Element;
    Overline: (props: TextComponentProps) => JSX.Element;
    H0: (props: TextComponentProps) => JSX.Element;
    H05: (props: TextComponentProps) => JSX.Element;
    H15: (props: TextComponentProps) => JSX.Element;
    H25: (props: TextComponentProps) => JSX.Element;
    H35: (props: TextComponentProps) => JSX.Element;
    Description16: (props: TextComponentProps) => JSX.Element;
    Description14: (props: TextComponentProps) => JSX.Element;
    ShortBoldText16: (props: TextComponentProps) => JSX.Element;
    ShortBoldText12: (props: TextComponentProps) => JSX.Element;
    ShortMediumText18: (props: TextComponentProps) => JSX.Element;
    ShortMediumText16: (props: TextComponentProps) => JSX.Element;
    ShortMediumText14: (props: TextComponentProps) => JSX.Element;
    ShortRegularText16: (props: TextComponentProps) => JSX.Element;
    ShortRegularText14: (props: TextComponentProps) => JSX.Element;
    ShortRegularText12: (props: TextComponentProps) => JSX.Element;
    ShortRegularText10: (props: TextComponentProps) => JSX.Element;
}

const Text: TextType = {
    Body1: (props): JSX.Element => <TextComponent {...props} variant="body1" />,
    Body2: (props): JSX.Element => <TextComponent {...props} variant="body2" />,
    Subtitle1: (props): JSX.Element => <TextComponent {...props} variant="subtitle1" />,
    Subtitle2: (props): JSX.Element => <TextComponent {...props} variant="subtitle2" />,
    H1: (props): JSX.Element => <TextComponent {...props} variant="h1" />,
    H2: (props): JSX.Element => <TextComponent {...props} variant="h2" />,
    H3: (props): JSX.Element => <TextComponent {...props} variant="h3" />,
    H4: (props): JSX.Element => <TextComponent {...props} variant="h4" />,
    Overline: (props): JSX.Element => <TextComponent {...props} variant="overline" />,
    H0: (props): JSX.Element => <TextComponent {...props} variant="h1" style={h0} />,
    H05: (props): JSX.Element => <TextComponent {...props} variant="h1" style={h05} />,
    H15: (props): JSX.Element => <TextComponent {...props} variant="h1" style={h15} />,
    H25: (props): JSX.Element => <TextComponent {...props} variant="h2" style={h25} />,
    H35: (props): JSX.Element => <TextComponent {...props} variant="h3" style={h35} />,
    Description16: (props): JSX.Element => <TextComponent {...props} variant="body1" style={description16} />,
    Description14: (props): JSX.Element => <TextComponent {...props} variant="body1" style={description14} />,
    ShortBoldText16: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortBoldText16} />,
    ShortBoldText12: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortBoldText12} />,
    ShortMediumText18: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortMediumText18} />,
    ShortMediumText16: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortMediumText16} />,
    ShortMediumText14: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortMediumText14} />,
    ShortRegularText16: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortRegularText16} />,
    ShortRegularText14: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortRegularText14} />,
    ShortRegularText12: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortRegularText12} />,
    ShortRegularText10: (props): JSX.Element => <TextComponent {...props} variant="body1" style={shortRegularText10} />,
};

const h0: React.CSSProperties = {
    fontSize: 32,
    fontWeight: 500, // 900,
    fontFamily: 'Madras',
};

const h05: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 500, // 900,
    fontFamily: 'Madras',
};

const h15: React.CSSProperties = {
    fontSize: 24,
    fontWeight: 500, // 900,
    fontFamily: 'Madras',
};

const h25: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 500, // 900,
    fontFamily: 'Madras',
};

const h35: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 500, // 900,
    fontFamily: 'Madras',
};

const shortBoldText16: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 500, // 800,
    letterSpacing: 0.2,
    fontFamily: 'Madras',
};

const shortBoldText12: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500, // 800,
    letterSpacing: 0.2,
    fontFamily: 'Madras',
};

const shortMediumText18: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 500,
    letterSpacing: 0.2,
};
const shortMediumText16: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 500,
    letterSpacing: 0.2,
};
const shortMediumText14: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 500,
    letterSpacing: 0.2,
};

const shortRegularText16: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 0.2,
};
const shortRegularText14: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 0.2,
};
const shortRegularText12: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 0.2,
};
const shortRegularText10: React.CSSProperties = {
    fontSize: 10,
    fontWeight: 400,
    letterSpacing: 0.2,
};

const description16: React.CSSProperties = {
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: 0.2,
};
const description14: React.CSSProperties = {
    fontSize: 14,
    fontWeight: 400,
    letterSpacing: 0.2,
};

export default Text;
